import { BUTTON_SIZE, BUTTON_TYPE, COLORS, DSButton, TAB_TYPE } from '@repo/design-system/index'
import { QIcon_check_filled } from '@repo/icons/index'
import { utcToZonedTime } from 'date-fns-tz'
import Pusher, { Options } from 'pusher-js'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import BodyClassName from 'react-body-classname'
import { useCookies } from 'react-cookie'
import { ReflexContainer, ReflexElement, ReflexSplitter } from 'react-reflex'
import 'react-reflex/styles.css'
import {
  getEventUserType,
  sendGTMBasicPageViewEvent,
  useEventTracking,
  useGTMBasicPageViewEventWithProperties,
} from '../../../../helpers/gtmHelper'
import { ISettingInfo } from '../../../../model/lesson.interface'
import apis from '../../../../modules/apis'
import { useClassroomDocs, useClassroomZoom } from '../../../../modules/apis/student/classroom'
import {
  HandshakePopupInfo,
  HandshakeSlotOpenEventData,
  HandshakeSlotsResponse,
} from '../../../../modules/apis/student/tutors/handshake/types'
import { axiosGet } from '../../../../modules/axiosHelper'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import { useThirdPartyCookieCheck } from '../../../../modules/hooks/common/useThirdPartyCookieCheck'
import useTypeQuery from '../../../../modules/hooks/common/useTypeQuery'
import useReturnFullUrl from '../../../../modules/hooks/webview/common/useReturnFullUrl'
import { useLocaleRedirect } from '../../../../modules/i18n/LocaleRedirect'
import { LOCALE_EN, LOCALE_KO } from '../../../../modules/i18n/config'
import {
  n_bonus_point,
  n_classroom,
  s_classroom,
  s_common,
  s_portal_home,
  s_preparation,
} from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { DATA_REF_KEY } from '../../../../modules/ids/e2e/student'
import { STUDENT_PAGE_VIEW_ID } from '../../../../modules/ids/gtm/student'
import { IMG_ZOOM_GUIDE_CLASSROOM_EN, IMG_ZOOM_GUIDE_CLASSROOM_KR } from '../../../../modules/images'
import urls from '../../../../modules/urls'
import {
  CDN_URL,
  COOKIE_OPT,
  COURSE_TYPES,
  LOCATION,
  PRE_STUDY,
  PUSHER_APP_KEY,
  SPEAKING_FOCUSED_FOR_INTERVIEW,
} from '../../../../modules/vars'
import { useBasicPopup } from '../../../basic/popup/BasicPopup'
import { useConfirmPopup } from '../../../basic/popup/ConfirmPopup'
import { TAB } from '../../../materials/const'
import { CONTENT_NAV_BAR_HEIGHT } from '../../../menu/ContentNavBar'
import GoogleTranslateBtn from '../../../new-portal/GoogleTranslateBtn'
import SubTab from '../../../new-portal/lessons/preparation/SubTab'
import ContentNavBar from '../../../new-portal/menu/ContentNavBar'
import LessonSettingConfirmPopup from '../lessons/LessonSettingConfirmPopup'
import HandshakeSlotExpiredModal from './HandshakeSlotExpiredModal'
import HandshakeToStudentPopup from './HandshakeToStudentPopup'
import PopupSettingModalBody from './PopupSettingModalBody'
import StudentClassroomRightBody, { StudentClassroomRightTabs } from './StudentClassroomRightBody'
import StudentClassroomTabBody from './StudentClassroomTabBody'
import { ClassroomPopupBody, HelpBtn, IShowPopup, MockSessionToggle, ZoomGuideBtn } from './StudentClassroomUtils'

/**
 * 체험수업 포인트 지급 버그로 인한 임시 비활성화 플래그 (현재 해결완료)
 * true = 모달 활성화
 * false = 모달 비활성화
 */
const TRIAL_POINT_POPUP_ENABLED = true

const pusherSubscribe = (t, pusher, channelName, onChange) => {
  const channel = pusher.subscribe(channelName)
  console.log('[Pusher] Subscribed to channel:', channelName)
  channel.bind('my_event', function (data) {
    console.log('[Pusher] Received message on channel:', channelName, 'data:', data)
    if (data && data.message) {
      const object = {
        user_id: data.sender_id,
        content: data.message,
        date: data.date,
      }
      console.log('[Pusher] Processed message object:', object)
      onChange(object)
    }
  })
}

type ClassroomPropsType = {
  json: any
}

const StudentClassroomBody = ({ json }: ClassroomPropsType) => {
  const basicPopup = useBasicPopup()
  const confirmPopup = useConfirmPopup()
  const isWebview = useIsWebview(false)
  const isMobile = useIsMobile(false)
  const { locale, isLocaleKO, t } = useTranslation()
  const router = useAppRouter()
  const { data: zoomData } = useClassroomZoom(router.query.lessonId as string)
  const { data: docsData } = useClassroomDocs(router.query.lessonId as string)

  const { currentUser } = useCurrentUser()
  const { typeQuery } = useTypeQuery()
  const localeRedirect = useLocaleRedirect()
  const { sendClickEvent } = useEventTracking()
  const getFullUrl = useReturnFullUrl()
  const docsRef = useRef(null)

  const lesson = json?.lesson
  const isTrial = json?.is_trial
  const chatLogs = json?.chatlogs || []
  const isUrgentPopup = json?.is_urgent_popup
  const isRepurchasePointTarget = json?.is_repurchase_point_target
  const isFreeTalking = lesson?.course?.free_talking
  const data = json?.lesson

  const [showGoogleTranslate, setShowGoogleTranslate] = useState(false)
  const [showZoomGuide, setShowZoomGuide] = useState(false)
  const [showChatBox, setShowChatBox] = useState(chatLogs.length > 0)
  const [chatList, setChatList] = useState(chatLogs)
  const [showPopup, setShowPopup] = useState<IShowPopup>({
    exitModal: false,
    evaluationModal: false,
    reportModal: false,
    reportConfirm: false,
  })

  const [ringleDocUrl, setRingleDocUrl] = useState('')
  const [documentUrl, setDocumentUrl] = useState('')
  const [ringleDocToken, setRingleDocToken] = useState('')

  useEffect(() => {
    if (docsData?.document_url) {
      setDocumentUrl(docsData?.document_url)
    }
    if (docsData?.ringledoc_url) {
      setRingleDocUrl(docsData?.ringledoc_url)
    }

    if (docsData?.ringledoc) {
      setRingleDocToken(docsData?.ringledoc)
    }
  }, [docsData])

  const PRESENCE_ALLCHANNEL = `presence-allchannel`
  const PRESENCE_LESSON_ID = `presence-${lesson?.id}`
  const ADMIN_CHANNEL_LESSON_ID_USER_ID = `admin_channel_${lesson?.id}_userid_${lesson?.student_id}`
  const SERVER_CHANNEL_NAME = `chat_with_admin_${lesson?.student_id}_web`

  const materialRef = useRef(null)

  const getDefaultTab = () => {
    if (typeQuery == PRE_STUDY.UPLOAD) {
      return TAB.UPLOAD_FILE
    } else if (lesson?.course.free_talking) {
      if (lesson?.course.course_type == 'upload_file') {
        return TAB.UPLOAD_FILE
      } else if (lesson?.course.course_type == COURSE_TYPES.DISCUSSION) {
        return TAB.UPLOAD_FILE
      } else return TAB.UPLOAD_LINK
    } else {
      return TAB.QUESTION_SELECT
    }
  }
  const [currentTab, setCurrentTab] = useState(getDefaultTab())

  const ZoomNoticeModal = useMemo(() => {
    const comp = () => {
      return (
        <div className="border-t text-center">
          <img src={isLocaleKO ? IMG_ZOOM_GUIDE_CLASSROOM_KR : IMG_ZOOM_GUIDE_CLASSROOM_EN} />
        </div>
      )
    }
    comp.displayName = 'ZoomNoticeModal'

    return comp
  }, [isLocaleKO])

  const sendGTMPVEvent = useGTMBasicPageViewEventWithProperties({
    page_id: STUDENT_PAGE_VIEW_ID.TRIAL_POP_UP,
    page_title: 'lesson',
    device_type: 'pc_web',
  })

  const showTrialLessonPopup = () => {
    sendGTMPVEvent()
    basicPopup.show({
      noTitle: true,
      id: 'trial-modal',
      maxW: 464,
      bodyPx: 0,
      bodyPb: 0,
      rounded: 'rounded-[12px]',
      noClose: true,
      onClose: () => {
        setCookies('trial_point_popup', true, COOKIE_OPT)
      },
      body: (
        <div className="bg-white p-[32px] rounded-[12px] text-center">
          <div className="Body1-16Md text-gray-700">{t(n_bonus_point.popup.subtitle_2)}</div>
          <div className="mt-[4px] Headline3-22Bd">{t(n_bonus_point.popup.title(currentUser?.profile?.name))}</div>
          <img
            src={`${CDN_URL}/web/ringle-student/lesson/img-point-popup.png`}
            alt="lesson-introduce-1"
            className="mt-[12px] h-[124px] mx-auto"
          />
          <div className="mt-[16px] Body1-16Bd text-purple-500">{t(n_bonus_point.popup.caption_24hours)}</div>
          <div className="mt-[4px] Body2-14Md text-gray-500">{t(n_bonus_point.popup.caption_24hour_desc)}</div>
          <div className="mt-[24px]">
            <DSButton type={BUTTON_TYPE.SOLID} size={BUTTON_SIZE.LG} customBgColor="#3C2BAC" onClick={handlePurchase}>
              <DSButton.Label>{isLocaleKO ? '지금 포인트 사용하기' : 'Get benefits points'}</DSButton.Label>
            </DSButton>
            <div
              className="mt-[16px] text-gray-500 Body1-16Md text-center cursor-pointer"
              onClick={() => {
                setCookies('trial_point_popup', true, COOKIE_OPT)
                basicPopup.close('trial-modal')
                setShowPopup({ ...showPopup, evaluationModal: true })
              }}
            >
              {t(s_portal_home.popup.welcome_coupon.btn_3)}
            </div>
          </div>
        </div>
      ),
    })
  }

  const showNotice = () => {
    confirmPopup.show({
      id: 'infoModal',
      title: t(s_classroom.modal_title),
      subtitle: t(s_classroom.modal_desc),
      body: <ZoomNoticeModal />,
      leftBtnLabel: t(s_classroom.btn_1),
      onLeftBtnClick: () => {
        setCookies('is_close_zoom_notice', true, COOKIE_OPT)
      },
      rightBtnLabel: t(s_classroom.btn_2),
    })
  }

  useEffect(() => {
    const pusher = new Pusher(PUSHER_APP_KEY, {
      authEndpoint: apis.lessons.pusherUrl(),
      auth: {
        params: {
          lesson_id: lesson?.id,
          user_id: lesson?.student_id,
          system: `Web`,
          encrypted: true,
        },
      },
    } as Options)
    pusher.subscribe(PRESENCE_ALLCHANNEL)
    pusher.subscribe(PRESENCE_LESSON_ID)

    pusherSubscribe(t, pusher, SERVER_CHANNEL_NAME, function (object) {
      // do nothing
    })

    pusherSubscribe(t, pusher, ADMIN_CHANNEL_LESSON_ID_USER_ID, function (object) {
      setShowChatBox(true)
      setChatList((chatList) => chatList.concat(object))
    })

    // handshake_slot_open 이벤트 리스너 추가
    const handshakeChannel = pusher.subscribe(ADMIN_CHANNEL_LESSON_ID_USER_ID)
    handshakeChannel.bind('handshake_slot_open', function (data: HandshakeSlotOpenEventData) {
      // HandshakeToStudentPopup 보여주기
      if (data.handshake_info) {
        setHandshakeInfo({
          tutorId: data.handshake_info.tutor_id || 0,
          tutorName: data.handshake_info.tutor_name || '',
          tutorImageUrl: data.handshake_info.tutor_image_https || '',
          scheduleDate: data.handshake_info.formatted_schedule_date || '',
          expiredTime: data.handshake_info.expired_time || '',
        })
        setShowHandshakeToStudentPopup(true)
      }
    })

    return function pusherUnsubscribe() {
      const lessonId = lesson?.id
      const userId = lesson?.student_id
      if (pusher !== null) {
        pusher.unsubscribe(`admin_channel_${lessonId}_userid_${userId}`)
        pusher.unsubscribe(`test_channel_${lessonId}`)
        pusher.unsubscribe('presence-allchannel')
        pusher.unsubscribe(ADMIN_CHANNEL_LESSON_ID_USER_ID)
      }
    }
  }, [])

  const activateDocs = () => {
    docsRef.current.style.display = ''
  }
  const deactivateDocs = () => {
    docsRef.current.style.display = 'none'
  }

  // 나가기 관련
  const handleOnClick = async (lessonId) => {
    console.info('🐛 classroom: on handleOnClick')
    const response = await axiosGet(apis.lessons.exit(lessonId))

    if (response.need_evaluation) {
      deactivateDocs()
      console.info('🐛 classroom: if(need_evaluation)')
      if (isTrial && !cookies.trial_point_popup && TRIAL_POINT_POPUP_ENABLED) {
        showTrialLessonPopup()
      } else if (isRepurchasePointTarget && !cookies.repurchase_point_popup) {
        showRepurchasePopup()
      } else {
        setShowPopup({ ...showPopup, evaluationModal: true })
      }
    } else {
      console.info('🐛 classroom: else(need_evaluation)')
      if (currentUser?.upcoming_lesson_count == 0 && currentUser?.unused_coupons > 0) {
        router.push(urls.student.newPortal.lessons.upcomingLessons)
      } else {
        if (isTrial && TRIAL_POINT_POPUP_ENABLED) {
          showTrialLessonPopup()
        } else {
          router.push(urls.student.newPortal.lessons.reviews.home)
        }
      }
    }
  }

  const handlePurchase = () => {
    router.push(urls.student.newPortal.pricing.index)
  }

  const showRepurchasePopup = () => {
    basicPopup.show({
      noTitle: true,
      id: 'success-modal',
      maxW: 464,
      bodyPx: 0,
      bodyPb: 0,
      rounded: 'rounded-[12px]',
      noClose: true,
      onClose: () => {
        setCookies('repurchase_point_popup', true, COOKIE_OPT)
      },
      body: (
        <div className="bg-white p-[32px] rounded-[12px] text-center">
          <div className="Body1-16Md text-gray-700">{t(n_bonus_point.popup.subtitle)}</div>
          <div className="mt-[4px] Headline3-22Bd">{t(n_bonus_point.popup.title(currentUser?.profile?.name))}</div>
          <img
            src={`${CDN_URL}/web/ringle-student/lesson/img-point-popup.png`}
            alt="lesson-introduce-1"
            className="mt-[12px] h-[124px] mx-auto"
          />
          <div className="mt-[16px] Body1-16Bd text-purple-500">{t(n_bonus_point.popup.caption)}</div>
          <div className="mt-[4px] grid grid-cols-1 gap-y-[4px] px-[20px]">
            <div className="flex items-center Body1-16Bd gap-x-[8px]">
              <QIcon_check_filled color={COLORS.PURPLE400} />
              <div>{t(n_bonus_point.popup.list_1)}</div>
            </div>
            <div className="flex items-center Body1-16Bd gap-x-[8px]">
              <QIcon_check_filled color={COLORS.PURPLE400} />
              <div>{t(n_bonus_point.popup.list_2)}</div>
            </div>
          </div>
          <div className="mt-[24px]">
            <DSButton type={BUTTON_TYPE.SOLID} size={BUTTON_SIZE.LG} customBgColor="#3C2BAC" onClick={handlePurchase}>
              <DSButton.Label>{isLocaleKO ? '지금 포인트 사용하기' : 'Get benefits points'}</DSButton.Label>
            </DSButton>
            <div
              className="mt-[16px] text-gray-500 Body1-16Md text-center cursor-pointer"
              onClick={() => {
                setCookies('repurchase_point_popup', true, COOKIE_OPT)
                basicPopup.close('success-modal')
                setShowPopup({ ...showPopup, evaluationModal: true })
              }}
            >
              {t(n_bonus_point.popup.btn_2)}
            </div>
          </div>
        </div>
      ),
    })
  }

  // 나가기 버튼 클릭
  const handleExit = async (e) => {
    e.preventDefault()
    console.info('🐛 classroom: on handleExit')
    const lesson = json?.lesson

    if (!lesson) {
      router.back()
      return
    }

    // event tracking
    sendClickEvent({ button_name: 'back' })
    const timezone = currentUser?.timezone || 'UTC'
    const lessonStartTime = lesson?.start_time ? new Date(lesson.start_time) : new Date()
    const lessonStartTimeZoned = utcToZonedTime(lessonStartTime, timezone)
    const lessonStartTimeUnix = Math.floor(lessonStartTimeZoned.getTime() / 1000)

    const currentTimeZoned = utcToZonedTime(new Date(), timezone)
    const currentTimeUnix = Math.floor(currentTimeZoned.getTime() / 1000)

    const lessonEndTime = lessonStartTimeUnix + lesson?.duration * 60

    // 현재 시간이 수업 종료 시간을 지나지 않았을 경우
    if (currentTimeUnix <= lessonEndTime) {
      console.info('🐛 classroom: if(currentTimeUnix <= lessonEndTime)')
      setShowPopup({ ...showPopup, exitModal: true })
    } else {
      console.info('🐛 classroom: else(currentTimeUnix <= lessonEndTime)')
      handleOnClick(lesson.id)
    }
  }

  const handleLessonTextClick = () => {
    confirmPopup.show({
      id: 'leaveConfirmModal',
      leftBtn: true,
      onRightBtnClick: () => {
        confirmPopup.close('leaveConfirmModal')
        localeRedirect(urls.student.portal.lessons.upcomingLessons)
      },
      rightBtnLabel: t(s_common.exit),
      title: <div>{t(s_classroom.leave_classroom)}</div>,
      body: <div>{t(s_classroom.leave_classroom_confirm)}</div>,
    })
  }

  useEffect(() => {
    if (currentUser) {
      sendGTMBasicPageViewEvent(
        STUDENT_PAGE_VIEW_ID.LESSON_CLASSROOM,
        getEventUserType(currentUser),
        locale,
        'Lesson Classroom',
        router.asPath
      )
    }
  }, [currentUser])

  /**
   * /j/ : join meeting link (zoom)
   */
  const cookieAllowStatus = useThirdPartyCookieCheck()
  const joinZoomUrl = useMemo(() => {
    if (zoomData?.join_url) {
      return zoomData?.join_url?.split('/')[3] == 'j'
        ? `${zoomData?.join_url}?zak=${zoomData?.zoom_url}`
        : `${zoomData?.join_url}`
    } else {
      return null
    }
  }, [zoomData])

  const showPopupBlockedModal = useCallback(() => {
    console.info('showPopupBlockedModal', '팝업 세팅 모달 띄움')
    confirmPopup.show({
      id: 'show-popup-blocked-modal',
      title: t(s_classroom.popup_block_title),
      subtitle: t(s_classroom.popup_block_subtitle),
      body: <PopupSettingModalBody locale={locale} />,
      leftBtn: false,
      rightBtn: false,
    })
  }, [confirmPopup, locale])

  const [cookies, setCookies] = useCookies(['is_close_zoom_notice', 'repurchase_point_popup', 'trial_point_popup'])

  const showInitialZoomNoticeModal = () => {
    /**
     * 첫 수업 && 유저가 다시보지 않기 체크하지 않았을 때에만 보여지는 줌 안내 팝업
     */
    const isCloseZoomNotice = cookies.is_close_zoom_notice === 'true'
    if (isWebview) return
    if (isUrgentPopup && !isCloseZoomNotice) {
      showNotice()
    }
  }

  const openZoomLinkWithNewTab = (url) => {
    const popup = window.open(url, '_blank')
    try {
      popup.focus()
    } catch (error) {
      // 팝업이 열리지 않는 경우(브라우저에 팝업 허용되지 않은 경우)
      // (1) 팝업허용 모달 노출
      if (!isWebview) {
        showPopupBlockedModal()
      }
      return
    }
  }

  useEffect(() => {
    console.info('joinZoomUrl student', lesson?.id, joinZoomUrl)
    // FIXME cookieAllowStatus이 undefined 상태로 계속 유지될 수 있는 케이스가 있음  -> 수정됨, 300ms 이상 응답이 없으면 false로 리턴
    if (cookieAllowStatus === undefined) {
      // 쿠키 허용상태 체크 이전에는 아무것도 하지 않음
      return
    }

    if (joinZoomUrl) {
      // (2) 줌오디오 관련 안내 팝업 모달 노출- 학생만
      showInitialZoomNoticeModal()
      if (cookieAllowStatus) {
        console.info('openZoomLinkWithiframe student', lesson?.id, joinZoomUrl)
        // 3rd party 쿠키 허용 상태가 true면 아래 hidden처리된 iframe에서 zoom link가 열림
      } else if (!isWebview) {
        //시크릿 브라우저 - 3rd party 쿠키 허용상태가 false면 새탭으로 강제로 zoom link를 열어줌
        openZoomLinkWithNewTab(joinZoomUrl)
        console.info('openZoomLinkWithNewTab student', lesson?.id, joinZoomUrl)
      }
    } else {
      console.info('joinZoomURL student null', lesson?.id)
    }
  }, [cookieAllowStatus, joinZoomUrl, isWebview])

  useEffect(() => {
    addEventListener('beforeunload', (e) => {
      e.preventDefault()
      console.info('🐛 classroom: beforeunload event')
    })

    return () => {
      removeEventListener('beforeunload', (e) => {
        e.preventDefault()
      })
    }
  }, [])

  const MATERIAL_SUB_TABS = [
    {
      dataRef: DATA_REF_KEY.btn_tab_insight,
      label: t(n_classroom.tab_insight),
      key: TAB.INSIGHT,
      none: data?.course?.id != SPEAKING_FOCUSED_FOR_INTERVIEW && isFreeTalking,
    },
    {
      label: t(n_classroom.tab_question),
      key: TAB.QUESTION_SELECT,
      none: isFreeTalking,
    },
    {
      label: t(s_preparation.document_submission),
      key: TAB.UPLOAD_FILE,
      none: !isFreeTalking,
    },
    {
      label: t(n_classroom.tab_video),
      key: TAB.MEDIA,
      none: isFreeTalking,
    },
  ]

  const CONTENT_SUB_NAV_BAR_HEIGHT = 52

  const [settingInfo, setSettingInfo] = useState<ISettingInfo>(null)
  const [showEnterPopup, setShowEnterPopup] = useState<boolean>(false)
  const [showHandshakeToStudentPopup, setShowHandshakeToStudentPopup] = useState<boolean>(false)
  const [showHandshakeExpiredModal, setShowHandshakeExpiredModal] = useState<boolean>(false)
  const [handshakeInfo, setHandshakeInfo] = useState<HandshakePopupInfo>({
    tutorId: 0,
    tutorName: '',
    tutorImageUrl: '',
    scheduleDate: '',
    expiredTime: '',
  })

  const handleLessonInfo = async () => {
    const url = `${apis.lessons.setting(lesson.id)}`
    const response = await axiosGet(url)
    if (response) {
      if (response.response) {
        setShowEnterPopup(true)
        setSettingInfo(response.response)
      }
    }
  }

  const handleHandshakeReservation = async () => {
    const tutorId = handshakeInfo.tutorId
    if (!tutorId) {
      console.error('No tutor ID available')
      return
    }

    try {
      // Call API to check if handshake slot is still valid
      const response = await axiosGet(apis.tutor.handshakeSlotsForTutor(tutorId))

      // Check if response contains valid slots
      const hasValidSlots =
        response &&
        response.timeslots &&
        Array.isArray(response.timeslots) &&
        response.timeslots.length > 0 &&
        response.timeslots.some(
          (dateGroup: HandshakeSlotsResponse[number]) => dateGroup.timeslots && dateGroup.timeslots.length > 0
        )

      if (!hasValidSlots) {
        // No valid slots - show the expired modal
        setShowHandshakeExpiredModal(true)
        return
      }

      // Valid slots exist - navigate to tutor's reservation page
      const targetUrl = urls.student.newPortal.tutors.detail.as(tutorId, 3)

      if (isWebview || isMobile) {
        router.push(targetUrl, {
          appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
          appSendData: {
            url: getFullUrl(urls.webview.tutors.detail.as(tutorId, 3)),
          },
        })
      } else {
        // Desktop: open in new tab with locale
        const fullUrl = `/${locale}${targetUrl}`
        window.open(fullUrl, '_blank')
      }
    } catch (error) {
      console.error('Failed to check handshake slots:', error)
      // Show error popup
      confirmPopup.show({
        id: 'handshake-error-modal',
        title: 'Error',
        body: 'Failed to check slot availability. Please try again.',
        leftBtn: true,
        leftBtnLabel: 'OK',
        rightBtn: false,
        onLeftBtnClick: () => {
          confirmPopup.close('handshake-error-modal')
        },
      })
    }
  }

  const handleExpiredModalClose = () => {
    setShowHandshakeExpiredModal(false)
  }

  const handleExpiredModalGoToCheck = () => {
    const tutorId = handshakeInfo.tutorId
    const targetUrl = urls.student.newPortal.tutors.detail.as(tutorId, 3)

    setShowHandshakeExpiredModal(false)

    if (isWebview || isMobile) {
      router.push(targetUrl, {
        appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
        appSendData: {
          url: getFullUrl(urls.webview.tutors.detail.as(tutorId, 3)),
        },
      })
    } else {
      // Desktop: open in new tab with locale
      const fullUrl = `/${locale}${targetUrl}`
      window.open(fullUrl, '_blank')
    }
  }

  return (
    <>
      <div className="hidden">{joinZoomUrl && cookieAllowStatus && <iframe src={joinZoomUrl}></iframe>}</div>
      <BodyClassName className="body h-full w-full" />
      {!isWebview && (
        <ContentNavBar
          location={LOCATION.LESSON}
          onExit={handleExit}
          handleLessonInfo={handleLessonInfo}
          pageType={
            <div onClick={handleLessonTextClick} className="cursor-pointer">
              {t(['수업', 'Lesson'])}
            </div>
          }
          description={lesson?.formatted_start_time}
          lesson={lesson}
          showGoogleTranslate={showGoogleTranslate}
          setShowGoogleTranslate={setShowGoogleTranslate}
          showZoomGuide={showZoomGuide}
          setShowZoomGuide={setShowZoomGuide}
          button2={
            (locale == LOCALE_KO || locale == LOCALE_EN) && (
              <GoogleTranslateBtn
                showGoogleTranslate={showGoogleTranslate}
                setShowGoogleTranslate={setShowGoogleTranslate}
              />
            )
          }
          button3={<ZoomGuideBtn showZoomGuide={showZoomGuide} setShowZoomGuide={setShowZoomGuide} />}
          button4={<HelpBtn showChatBox={showChatBox} setShowChatBox={setShowChatBox} />}
          showChatBox={showChatBox}
          setShowChatBox={setShowChatBox}
          chatList={chatList}
          isLesson
        />
      )}
      <div className="h-px w-full bg-gray-200"></div>
      <div style={{ height: `calc(100vh - ${CONTENT_NAV_BAR_HEIGHT + 1}px)` }}>
        <ReflexContainer orientation="vertical">
          {!isMobile && lesson && (
            <ReflexElement minSize={250} className="left-pane no-scrollbar overflow-y-scroll">
              <SubTab
                SUB_MENU_LIST={MATERIAL_SUB_TABS}
                activeTabIdx={currentTab}
                setActiveTabIdx={setCurrentTab}
                type={TAB_TYPE.SCROLL}
                lesson={lesson}
                currentUser={currentUser}
                isRightTab={false}
              />
              <StudentClassroomTabBody
                materialRef={materialRef}
                lesson={lesson}
                currentTab={currentTab}
                currentUser={currentUser}
              />
            </ReflexElement>
          )}
          {!isMobile && (
            <ReflexSplitter
              style={{
                height: `calc(100vh - ${CONTENT_NAV_BAR_HEIGHT + CONTENT_SUB_NAV_BAR_HEIGHT}px)`,
                position: 'relative',
                top: CONTENT_SUB_NAV_BAR_HEIGHT,
              }}
            />
          )}
          <ReflexElement minSize={250} className="right-pane">
            <StudentClassroomRightTabs ringleDocUrl={ringleDocUrl} ringleDocToken={ringleDocToken} />
            <StudentClassroomRightBody
              docsRef={docsRef}
              //링글닥과 구글닥 구분해서 보내기
              documentUrl={documentUrl}
              ringledocUrl={ringleDocUrl}
              ringleDocToken={ringleDocToken}
              currentUser={currentUser}
              specialLesson={lesson?.special_lesson}
            />
          </ReflexElement>
        </ReflexContainer>
        {json?.is_mock && <MockSessionToggle tutor={json?.lesson?.tutor} />}
      </div>
      {/* 나가기 모달 / 리포트 모달 / 수업 평가 모달 */}
      <ClassroomPopupBody
        showPopup={showPopup}
        setShowPopup={setShowPopup}
        handleModalRightButton={() => {
          handleOnClick(lesson?.id)
        }}
        activateDocs={() => activateDocs()}
        isClassroom={true}
      />
      {settingInfo && (
        <LessonSettingConfirmPopup
          item={lesson}
          settingInfo={settingInfo}
          showEnterPopup={showEnterPopup}
          setShowEnterPopup={setShowEnterPopup}
          handleAvatarClick={null}
          handleLessonStyleClick={null}
          inClass={true}
        />
      )}
      {/* HandshakeToStudentPopup - triggered by handshake_slot_open event */}
      <HandshakeToStudentPopup
        showPopup={showHandshakeToStudentPopup}
        setShowPopup={setShowHandshakeToStudentPopup}
        handshakeInfo={handshakeInfo}
        onReservationClick={handleHandshakeReservation}
      />
      {/* HandshakeSlotExpiredModal - shown when slots are no longer available */}
      {showHandshakeExpiredModal && (
        <HandshakeSlotExpiredModal
          tutorName={handshakeInfo.tutorName}
          onClose={handleExpiredModalClose}
          onGoToCheck={handleExpiredModalGoToCheck}
        />
      )}
    </>
  )
}

export default StudentClassroomBody
